<template>
  <NtClientOnly>
    <Teleport v-if="url" to="#teleported">
      <!-- Both variants are mobile-only (`md:hidden`): on desktop the CTA
           overlaps the header and page content, and the valuation link is
           already reachable from the navigation there. -->
      <!-- Boulevard: panel pinned top-right, directly under the header. Slides in from the right + fades in. -->
      <Transition
        v-if="isPanel"
        enter-active-class="transition duration-500 ease-out"
        enter-from-class="opacity-0 translate-x-1/4"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition duration-300 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="opacity-0 translate-x-1/4">
        <div
          v-if="visible"
          class="valuation-cta-panel fixed top-34 right-0 z-60 w-72 max-w-[calc(100vw-1.5rem)] bg-primary-500 px-8 py-10 text-center text-white shadow-2xl md:hidden">
          <button
            type="button"
            class="absolute top-3 right-3 flex h-8 w-8 cursor-pointer items-center justify-center text-white/70 transition-colors hover:text-white"
            aria-label="Dismiss"
            @click="close">
            <i class="fa-sharp fa-regular fa-xmark text-xl" aria-hidden="true"></i>
          </button>

          <!-- The default wording is set in three lines with the last word in a
               serif italic, which is the designed treatment. A custom label has
               no such shape to follow, so it is rendered plainly rather than
               having the flourish applied to whatever its last word happens to
               be. -->
          <p class="font-heading text-3xl leading-tight">
            <template v-if="customLabel">
              <span class="font-light">{{ customLabel }}</span>
            </template>
            <template v-else>
              <span class="font-light">Get an instant<br />online</span><br /><span class="valuation-cta-serif italic">valuation</span>
            </template>
          </p>

          <a
            :href="url"
            class="mt-6 inline-flex items-center justify-center gap-3 rounded-full bg-secondary-500 px-8 py-3 text-sm font-bold tracking-widest text-white uppercase transition-opacity hover:opacity-90">
            Go Now <i class="fa-sharp fa-regular fa-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
      </Transition>

      <!-- Plaza + fallback: full-width thin bar pinned to the top. Fades in + slides down.
           Atlas pins its own header to the top, which the bar would cover, so
           there it sits at the bottom of the screen and slides up instead. -->
      <Transition
        v-else
        enter-active-class="transition duration-300 ease-out"
        :enter-from-class="`${barOffscreenClass} opacity-0`"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        :leave-to-class="`${barOffscreenClass} opacity-0`">
        <a
          v-if="visible"
          :href="url"
          :class="atBottom ? 'bottom-0' : 'top-0'"
          class="valuation-cta-bar fixed inset-x-0 z-60 flex items-center justify-center gap-2 bg-primary-500 px-4 py-3 text-center text-2xl font-light tracking-wide md:hidden">
          {{ label }}
          <i class="fa-sharp fa-regular fa-chevron-right valuation-cta-nudge" aria-hidden="true"></i>
        </a>
      </Transition>
    </Teleport>
  </NtClientOnly>
</template>

<script>
import { router } from '@inertiajs/vue3'
import NtClientOnly from '@/js/components/general/NtClientOnly.vue'
import { SITE_EVENTS } from '@/js/events.js'

// How far the visitor must scroll before the CTA appears.
const SHOW_AFTER = 150

// Set by NtCookieConsent once the visitor has chosen; no cookie means its
// banner is showing.
const CONSENT_COOKIE = 'nt_cookie_consent'

export default {
  components: { NtClientOnly },
  data() {
    return {
      scrolled: false,
      dismissed: false,
      // Whether the cookie consent banner is up. Read in mounted(); false here
      // so the server render and first client render agree.
      consentPending: false
    }
  },
  computed: {
    url() {
      return this.$getIntegration('valuation_cta')?.valuation_url || null
    },
    /**
     * The admin's own wording, or null to use the designed default.
     *
     * Trimmed so a field containing only spaces reads as unset rather than
     * rendering an empty call to action.
     */
    customLabel() {
      return this.$getIntegration('valuation_cta')?.label?.trim() || null
    },
    label() {
      return this.customLabel ?? 'Get an instant online valuation'
    },
    // Templates whose header stays pinned to the top of the screen.
    atBottom() {
      return this.$page.props.website?.base_template_uid === 'atlas'
    },
    barOffscreenClass() {
      return this.atBottom ? 'translate-y-full' : '-translate-y-full'
    },
    isPanel() {
      return this.$page.props.website?.base_template_uid === 'boulevard'
    },
    visible() {
      // At the bottom the bar would sit on the cookie consent banner, so it
      // waits until the visitor has made their choice.
      const blockedByConsent = this.atBottom && this.consentPending

      return !!this.url && this.scrolled && !this.dismissed && !blockedByConsent
    }
  },
  mounted() {
    this.onScroll()
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.consentPending = !new RegExp(`(?:^|; )${CONSENT_COOKIE}=`).test(document.cookie)
    window.addEventListener(SITE_EVENTS.gdprConsented, this.onConsentChosen)
    window.addEventListener(SITE_EVENTS.gdprDenied, this.onConsentChosen)
    window.addEventListener(SITE_EVENTS.openCookieConsent, this.onConsentReopened)
    // Dismissal only lasts the current page: a fresh navigation lets it show
    // again (and the new page starts scrolled to the top, so it re-hides until
    // the visitor scrolls).
    this.removeNavListener = router.on('navigate', () => {
      this.dismissed = false
      this.onScroll()
    })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener(SITE_EVENTS.gdprConsented, this.onConsentChosen)
    window.removeEventListener(SITE_EVENTS.gdprDenied, this.onConsentChosen)
    window.removeEventListener(SITE_EVENTS.openCookieConsent, this.onConsentReopened)
    this.removeNavListener?.()
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > SHOW_AFTER
    },
    close() {
      this.dismissed = true
    },
    onConsentChosen() {
      this.consentPending = false
    },
    // The banner can be brought back (e.g. from a footer link).
    onConsentReopened() {
      this.consentPending = true
    }
  }
}
</script>

<style scoped>
/* The Boulevard panel renders the "valuation" flourish in Libre Baskerville
   (matching the live site), falling back to a generic serif if unavailable. */
.valuation-cta-serif {
  font-family: 'Libre Baskerville', serif;
}

/* Bar text: white by default, flipping to (near-)black only when the brand
   primary is very light (lightness >= 0.7) so it stays legible on pale brands.
   Uses the same relative-colour approach as the palette tokens in app.css. */
.valuation-cta-bar {
  color: oklch(from var(--color-primary-500) calc(1 - clamp(0, calc((l - 0.7) * 100), 1)) 0 0);
}

/* The bar's chevron nudges left/right to draw the eye. */
.valuation-cta-nudge {
  animation: valuation-cta-nudge 1s ease-in-out infinite;
}
@keyframes valuation-cta-nudge {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(5px);
  }
}
</style>
